/**
 * Cart Progress Bar
 * Handles progress tracking for free shipping and gift thresholds
 */

(function() {
  'use strict';

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initProgressBar();
      // Set initial state immediately without animation
      updateProgressBar(false);
    });
  } else {
    // DOM is already ready
    initProgressBar();
    // Set initial state immediately without animation
    updateProgressBar(false);
  }

  // Listen for cart updates via MinimogEvents
  if (window.MinimogEvents) {
    window.MinimogEvents.subscribe('ON_CART_UPDATE', function() {
      updateProgressBar();
    });
  } else {
    // Wait for MinimogEvents to be available
    let checkInterval = setInterval(function() {
      if (window.MinimogEvents) {
        clearInterval(checkInterval);
        window.MinimogEvents.subscribe('ON_CART_UPDATE', function() {
          updateProgressBar();
        });
      }
    }, 100);
  }

  // Also listen when cart drawer opens
  document.addEventListener('click', function(e) {
    if (e.target.closest('.sf-cart-icon, .cart-icon, [data-cart-drawer-toggle], [data-open-cart-drawer]')) {
      setTimeout(function() {
        updateProgressBar();
        initGiftSelection();
      }, 300);
    }
  });

  /**
   * Initialize progress bar event handlers
   */
  function initProgressBar() {
    initGiftSelection();
  }

  /**
   * Update progress bar when cart changes
   */
  async function updateProgressBar(animate = true) {
    try {
      // Fetch current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Get progress bar container
      const progressBar = document.querySelector('[data-cart-progress]');
      if (!progressBar) return;

      // Get thresholds and messages from data attributes
      // These are already sorted by threshold in Liquid (lowest first)
      const step1Threshold = parseInt(progressBar.dataset.step1Threshold) || 0;
      const step2Threshold = parseInt(progressBar.dataset.step2Threshold) || 0;
      const step1Enabled = progressBar.dataset.step1Enabled === 'true';
      const step2Enabled = progressBar.dataset.step2Enabled === 'true';

      // Step types — needed to know which step is the gift step (it can live
      // in the step1 slot when only the gift step is enabled).
      const step1Type = progressBar.dataset.step1Type || '';
      const step2Type = progressBar.dataset.step2Type || '';

      // Get message templates (already sorted by Liquid)
      const step1Message = progressBar.dataset.step1Message || 'Køb for [amount] mere og få gratis fragt!';
      const step1ReachedMessage = progressBar.dataset.step1ReachedMessage || 'Du får gratis fragt!';
      const step2Message = progressBar.dataset.step2Message || 'Køb for [amount] mere og få en gratis gave!';
      const step2ReachedMessage = progressBar.dataset.step2ReachedMessage || 'Du har optjent en gratis gave!';

      const cartTotal = cart.total_price;

      // Calculate progress
      let progressPercent = 0;
      let currentStep = 0;
      let message = '';

      // Format money to match Shopify's format
      const formatMoney = (cents) => {
        const amount = (cents / 100).toFixed(2);
        return amount.replace('.', ',') + ' kr';
      };

      if (step2Enabled && cartTotal >= step2Threshold && step2Threshold > 0) {
        progressPercent = 100;
        currentStep = 2;
        message = step2ReachedMessage;
      } else if (step1Enabled && cartTotal >= step1Threshold && step1Threshold > 0) {
        if (step2Enabled && step2Threshold > 0) {
          progressPercent = 50 + ((cartTotal - step1Threshold) / (step2Threshold - step1Threshold)) * 50;
          const remaining = step2Threshold - cartTotal;
          const amountFormatted = formatMoney(remaining);
          const amountWithStyling = '<strong class="cart-progress-amount">' + amountFormatted + '</strong>';
          message = step2Message.replace('[amount]', amountWithStyling);
        } else {
          progressPercent = 100;
          message = step1ReachedMessage;
        }
        currentStep = 1;
      } else if (step1Enabled && step1Threshold > 0) {
        // Working towards step 1
        const maxPercent = (step2Enabled && step2Threshold > 0) ? 50 : 100;
        progressPercent = (cartTotal / step1Threshold) * maxPercent;
        const remaining = step1Threshold - cartTotal;
        const amountFormatted = formatMoney(remaining);
        const amountWithStyling = '<strong class="cart-progress-amount">' + amountFormatted + '</strong>';
        message = step1Message.replace('[amount]', amountWithStyling);
      } else if (step2Enabled && step2Threshold > 0) {
        // Only step 2 enabled, working towards it
        progressPercent = (cartTotal / step2Threshold) * 100;
        const remaining = step2Threshold - cartTotal;
        const amountFormatted = formatMoney(remaining);
        const amountWithStyling = '<strong class="cart-progress-amount">' + amountFormatted + '</strong>';
        message = step2Message.replace('[amount]', amountWithStyling);
      }

      // Update DOM elements
      updateProgressDOM(progressPercent, currentStep, message, animate);

      // Decide gift selector visibility based on which step is the gift step.
      // The gift step can sit in either slot (e.g. it moves into the step1
      // slot when only the gift step is enabled), so key off the type — not
      // the slot — to avoid hiding a server-rendered gift selector.
      let giftStepReached = false;
      if (step1Enabled && step1Type === 'gift' && step1Threshold > 0 && cartTotal >= step1Threshold) {
        giftStepReached = true;
      }
      if (step2Enabled && step2Type === 'gift' && step2Threshold > 0 && cartTotal >= step2Threshold) {
        giftStepReached = true;
      }

      if (giftStepReached) {
        initGiftSelection();
        // Re-check if gift should be shown (in case it was added/removed)
        checkAndShowGiftSelector(cart);
      } else {
        hideGiftSelector();
      }

    } catch (error) {
      console.error('Error updating progress bar:', error);
    }
  }

  /**
   * Update progress bar DOM elements
   */
  function updateProgressDOM(percent, step, message, animate = true) {
    // Update progress fill
    const progressFill = document.querySelector('.cart-progress-fill');
    if (progressFill) {
      if (!animate) {
        // Disable transition temporarily for instant update
        progressFill.style.transition = 'none';
        progressFill.style.width = percent + '%';
        // Force reflow
        progressFill.offsetHeight;
        // Re-enable transition
        progressFill.style.transition = '';
      } else {
        progressFill.style.width = percent + '%';
      }
    }

    // Update message
    const messageEl = document.querySelector('.cart-progress-message');
    if (messageEl) {
      messageEl.innerHTML = message;
    }

    // Update step states
    document.querySelectorAll('.cart-progress-step').forEach((stepEl, index) => {
      if (index === 0 && step >= 1) {
        stepEl.classList.add('reached');
      } else if (index === 1 && step >= 2) {
        stepEl.classList.add('reached');
      } else {
        stepEl.classList.remove('reached');
      }

      // Update label colors
      const label = stepEl.querySelector('.cart-progress-step-label');
      if (label) {
        if ((index === 0 && step >= 1) || (index === 1 && step >= 2)) {
          label.classList.add('reached');
        } else {
          label.classList.remove('reached');
        }
      }
    });
  }

  /**
   * Initialize gift selection functionality
   */
  function initGiftSelection() {
    const addButtons = document.querySelectorAll('[data-gift-selector] [data-add-gift]');

    if (!addButtons.length) return;

    // Remove existing listeners by cloning
    addButtons.forEach(button => {
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
    });

    // Re-select after cloning
    const freshButtons = document.querySelectorAll('[data-gift-selector] [data-add-gift]');

    // Add click handler to each button
    freshButtons.forEach(button => {
      button.addEventListener('click', async function(e) {
        e.preventDefault();

        const variantId = this.dataset.variantId;
        const productHandle = this.dataset.productHandle;
        if (!variantId) return;

        // Add loading state
        this.classList.add('loading');

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              id: parseInt(variantId),
              quantity: 1,
              properties: {
                '_gift_product': 'true'
              }
            })
          });

          if (response.ok) {
            const cartData = await response.json();

            // Get the added item data
            const addedItem = cartData;

            // Mark button as added
            const btnText = this.querySelector('.btn-text');
            const btnIcon = this.querySelector('.btn-icon');
            this.classList.remove('loading');
            this.classList.add('added');
            if (btnText) btnText.textContent = 'Tilføjet';
            if (btnIcon) btnIcon.innerHTML = '<svg fill="currentColor" viewBox="0 0 24 24"><path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>';
            this.disabled = true;

            // Mark the gift item as added
            const giftItem = this.closest('.cart-gift-item');
            if (giftItem) {
              giftItem.classList.add('item-added');
            }

            // Mark selector as gift-added (triggers animation)
            const selector = document.querySelector('[data-gift-selector]');
            if (selector) {
              selector.classList.add('gift-added');
              selector.dataset.giftInCartHandle = productHandle;
            }

            // Trigger item added event
            if (window.Shopify && window.Shopify.onItemAdded) {
              window.Shopify.onItemAdded(addedItem);
            }

            // Trigger cart update with cart data
            if (window.MinimogEvents) {
              window.MinimogEvents.emit('ON_CART_UPDATE', cartData);
            }

            // Refresh cart drawer if MinimogTheme is available
            if (window.MinimogTheme && window.MinimogTheme.Cart) {
              window.MinimogTheme.Cart.renderNewCart();
            }

            // Also try the Shopify cart update function
            if (window.Shopify && window.Shopify.onCartUpdate) {
              window.Shopify.onCartUpdate();
            }

            // Update progress bar immediately
            setTimeout(async () => {
              await updateProgressBar();
            }, 100);

          } else {
            throw new Error('Failed to add gift');
          }
        } catch (error) {
          console.error('Error adding gift:', error);
          this.classList.remove('loading');
          const btnText = this.querySelector('.btn-text');
          if (btnText) btnText.textContent = 'Prøv igen';
          setTimeout(() => {
            this.disabled = false;
            if (btnText) btnText.textContent = 'Tilføj';
          }, 2000);
        }
      });
    });
  }

  /**
   * Check if gift is in cart and show/hide selector accordingly
   */
  function checkAndShowGiftSelector(cart) {
    const selector = document.querySelector('[data-gift-selector]');
    if (!selector) return;

    // Get gift product handles from data attributes
    const giftOptions = selector.querySelectorAll('[data-gift-handle]');
    const giftHandles = Array.from(giftOptions).map(opt => opt.dataset.giftHandle).filter(h => h);

    // Get gift variant IDs from data attributes
    const giftVariantIds = Array.from(selector.querySelectorAll('[data-add-gift]'))
      .map(btn => parseInt(btn.dataset.variantId))
      .filter(id => id);

    // Find the gift step's threshold (the gift step can sit in either slot).
    const progressBar = document.querySelector('[data-cart-progress]');
    let giftThreshold = 0;
    if (progressBar) {
      const s1 = parseInt(progressBar.dataset.step1Threshold) || 0;
      const s2 = parseInt(progressBar.dataset.step2Threshold) || 0;
      if (progressBar.dataset.step1Type === 'gift' && progressBar.dataset.step1Enabled === 'true') {
        giftThreshold = s1;
      }
      if (progressBar.dataset.step2Type === 'gift' && progressBar.dataset.step2Enabled === 'true') {
        giftThreshold = s2;
      }
    }

    // Check if any gift is already in cart and which one.
    // A gift only counts as a free gift if the cart stays above the threshold
    // once that gift item's own price is removed — otherwise the gift item is
    // what pushes the cart over (e.g. the customer bought it normally), so it
    // should not block the gift selector.
    let giftInCart = false;
    let giftInCartHandle = '';

    cart.items.forEach(item => {
      const matchesGift =
        giftVariantIds.includes(item.variant_id) ||
        giftVariantIds.includes(item.id) ||
        (item.handle && giftHandles.includes(item.handle)) ||
        (item.product_handle && giftHandles.includes(item.product_handle));

      if (matchesGift && (cart.total_price - item.final_price) >= giftThreshold) {
        giftInCart = true;
        giftInCartHandle = item.product_handle || item.handle;
      }
    });

    if (!giftInCart) {
      // No gift in cart - show selector normally
      selector.classList.add('active');
      selector.classList.remove('gift-added');
      selector.style.display = 'block';

      // Reset all items and buttons
      selector.querySelectorAll('.cart-gift-item').forEach(item => {
        item.classList.remove('item-added');
      });
      selector.querySelectorAll('.cart-gift-add-btn').forEach(btn => {
        btn.classList.remove('added');
        btn.disabled = false;
        const btnText = btn.querySelector('.btn-text');
        const btnIcon = btn.querySelector('.btn-icon');
        if (btnText) btnText.textContent = 'Tilføj';
        if (btnIcon) btnIcon.innerHTML = '<svg fill="currentColor" viewBox="0 0 24 24"><path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/></svg>';
      });
    } else {
      // Gift is in cart - hide selector completely
      selector.classList.remove('active');
      selector.style.display = 'none';
    }
  }

  /**
   * Show gift selector
   */
  function showGiftSelector() {
    const selector = document.querySelector('[data-gift-selector]');
    if (selector) {
      selector.classList.add('active');
      selector.style.display = 'block';
    }
  }

  /**
   * Hide gift selector
   */
  function hideGiftSelector() {
    const selector = document.querySelector('[data-gift-selector]');
    if (selector) {
      selector.classList.remove('active');
      selector.style.display = 'none';
    }
  }

})();